import type { Plan, PlansResponse } from "$lib/types";
import { getHttpStatusMessage } from "$lib/utility/httpErrorHelper";

export interface CreditsBalance {
  total: number;
  recurring: number;
  topup: number;
}

export interface CodeApplyResponse {
  success: boolean;
  message: string;
  codeType?: string;
  applied?: boolean;
}

export class SubscriptionAPI {
  private static readonly API_URL = "/api/v1";
  private static readonly BASE_URL = "/api/v1/billing";
  private static readonly ENDPOINTS = {
    checkout: "/checkout",
    manage: "/manage",
    plans: "/plans",
    credits: "/credits",
    creditsBalance: "/credits/balance",
    update: "/update",
    applyCode: "/codes/apply",
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  /**
   * Retrieves the current credits balance
   * @throws Error if the fetch request fails
   */
  static async getCreditsBalance(): Promise<CreditsBalance> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.creditsBalance}`, {
        method: "GET",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      return data as CreditsBalance;
    } catch (error) {
      console.error("Error fetching credits balance:", error);
      throw new Error(`Failed to get credits balance: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static getRewardfulReferral(): string | undefined {
    const rewardfulCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_rfsn="))
      ?.split("=")[1];

    return rewardfulCookie ? decodeURIComponent(rewardfulCookie) : undefined;
  }

  /**
   * Starts a Stripe checkout session for a specific plan.
   * @param plan - Plan label
   * @param rewardfulId - Optional Rewardful referral ID (falls back to URL param if not provided)
   */
  static checkout(plan: string, rewardfulId?: string): void {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${this.BASE_URL}${this.ENDPOINTS.checkout}`;

    const planInput = document.createElement("input");
    planInput.type = "hidden";
    planInput.name = "plan";
    planInput.value = plan;
    form.appendChild(planInput);

    const rewardful =
      rewardfulId || new URLSearchParams(window.location.search).get("ref") || this.getRewardfulReferral();

    if (rewardful) {
      const rewardfulInput = document.createElement("input");
      rewardfulInput.type = "hidden";
      rewardfulInput.name = "referral";
      rewardfulInput.value = rewardful;
      form.appendChild(rewardfulInput);
      // logger.log("Including Rewardful ID in checkout:", rewardful);
    }

    const referralCode = document.cookie
      .split("; ")
      .find((row) => row.startsWith("referral_code="))
      ?.split("=")[1];

    if (referralCode) {
      const referralInput = document.createElement("input");
      referralInput.type = "hidden";
      referralInput.name = "referralCode";
      referralInput.value = decodeURIComponent(referralCode);
      form.appendChild(referralInput);
      // logger.log("Including referral code in checkout:", decodeURIComponent(referralCode));
    }

    // CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "_csrf";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  /**
   * Applies a promotion code (referral code, discount code, etc.) to the user's account
   * @param codeText - The promotion code to apply
   * @throws Error if the fetch request fails
   */
  static async applyCode(codeText: string, currentPlan?: string): Promise<CodeApplyResponse> {
    try {
      const response = await fetch(`${this.API_URL}${this.ENDPOINTS.applyCode}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
        body: JSON.stringify({ codeText, currentPlan }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || getHttpStatusMessage(response.status));
      }

      return data as CodeApplyResponse;
    } catch (error) {
      console.error("Error applying promotion code:", error);
      throw new Error(`Failed to apply code: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Starts a Stripe billing portal session.
   */
  static manage(): void {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${this.BASE_URL}${this.ENDPOINTS.manage}`;

    // Set up csrf token if needed
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "_csrf";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add form to document and submit
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  /**
   * Retrieves all available plans and whether the user is subscribed to them.
   * This now includes both subscription plans and credit plans.
   * @throws Error if the fetch request fails
   */
  static async getPlans(): Promise<Plan[]> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.plans}`, {
        method: "GET",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data: PlansResponse = await response.json();
      if (data.plans && Array.isArray(data.plans)) {
        return data.plans;
      } else {
        throw new Error("Invalid response from plans endpoint: missing or invalid plans array");
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      throw new Error(`Failed to get plans: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  // /**
  //  * Retrieves filtered plans based on specified prefixes (defaults to "pro" and "plus").
  //  * Plans are filtered based on case-insensitive prefix matching.
  //  * @param prefixes Optional array of plan name prefixes to filter by
  //  * @throws Error if the fetch request fails
  //  */
  // static async getProductPlans(prefixes: string[] = ["plus", "pro", "team"]): Promise<Plan[]> {
  //   try {
  //     const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.plans}`, {
  //       method: "GET",
  //       headers: {
  //         [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
  //       },
  //       credentials: "include",
  //     });

  //     if (!response.ok) {
  //       const errorText = await response.text();
  //       throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
  //     }

  //     const data: PlansResponse = await response.json();
  //     if (!data.plans || !Array.isArray(data.plans)) {
  //       throw new Error("Invalid response from plans endpoint: missing or invalid plans array");
  //     }

  //     // Mock data for development
  //     // return [
  //     //   { name: "plus_tier_1_monthly", active: false, recurringCredits: 2500 },
  //     //   { name: "plus_tier_1_annual", active: false, recurringCredits: 2500 },
  //     //   { name: "plus_tier_2_monthly", active: false, recurringCredits: 5000 },
  //     //   { name: "plus_tier_2_annual", active: false, recurringCredits: 5000 },
  //     //   { name: "pro_tier_1_monthly", active: false, recurringCredits: 25000 },
  //     //   { name: "pro_tier_1_annual", active: false, recurringCredits: 25000 },
  //     //   { name: "pro_tier_2_monthly", active: false, recurringCredits: 40000 },
  //     //   { name: "pro_tier_2_annual", active: false, recurringCredits: 40000 },
  //     //   { name: "pro_tier_3_monthly", active: false, recurringCredits: 80000 },
  //     //   { name: "pro_tier_3_annual", active: false, recurringCredits: 80000 },
  //     // ];

  //     // Filter plans based on provided prefixes (case-insensitive)
  //     return data.plans.filter((plan) =>
  //       prefixes.some((prefix) => plan.name.toLowerCase().startsWith(prefix.toLowerCase())),
  //     );
  //   } catch (error) {
  //     console.error("Error fetching product plans:", error);
  //     throw new Error(`Failed to get product plans: ${error instanceof Error ? error.message : "Unknown error"}`);
  //   }
  // }

  /**
   * Retrieves filtered plans based on specified prefixes (defaults to "plus", "pro", "team").
   * Plans are filtered based on case-insensitive prefix matching.
   * @param prefixesOrFetch Optional array of plan name prefixes to filter by, or a fetch function for server-side rendering
   * @param serverFetch Optional fetch function for server-side rendering when prefixes are also provided
   * @throws Error if the fetch request fails
   */
  static async getProductPlans(
    prefixesOrFetch: string[] | typeof fetch = ["plus", "pro", "team", "scale"],
    serverFetch?: typeof fetch,
  ): Promise<Plan[]> {
    try {
      // Determine if first parameter is prefixes array or fetch function
      let prefixes: string[] = ["plus", "pro", "team", "scale"];
      let fetchFn: typeof fetch = fetch;

      if (typeof prefixesOrFetch === "function") {
        // First parameter is a fetch function
        fetchFn = prefixesOrFetch;
      } else {
        // First parameter is prefixes array
        prefixes = prefixesOrFetch;
        // Use the serverFetch if provided
        if (serverFetch) {
          fetchFn = serverFetch;
        }
      }

      const response = await fetchFn(`${this.BASE_URL}${this.ENDPOINTS.plans}`, {
        method: "GET",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data: PlansResponse = await response.json();
      if (!data.plans || !Array.isArray(data.plans)) {
        throw new Error("Invalid response from plans endpoint: missing or invalid plans array");
      }

      // Filter plans based on provided prefixes (case-insensitive)
      return data.plans.filter((plan) =>
        prefixes.some((prefix) => plan.name.toLowerCase().startsWith(prefix.toLowerCase())),
      );
    } catch (error) {
      console.error("Error fetching product plans:", error);
      throw new Error(`Failed to get product plans: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  /**
   * Updates the current subscription or credits plan to a new one.
   * @param currentPlan - The name of the current plan, e.g., "research" or "credits_silver"
   * @param newPlan - The name of the new plan to switch to
   * @throws Error if the fetch request fails
   */
  static async updatePlan(currentPlan: string, newPlan: string): Promise<boolean> {
    try {
      // logger.log(`Updating plan from ${currentPlan} to ${newPlan}`);
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.update}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
        body: JSON.stringify({ currentPlan, newPlan }),
      });

      if (!response.ok) {
        // const errorText = await response.text();
        throw new Error(getHttpStatusMessage(response.status));
      }

      // logger.log(`Successfully updated plan from ${currentPlan} to ${newPlan}`);

      return true;
    } catch (error) {
      console.error("Error updating plan:", error);
      throw new Error(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }
}
